import React, { useEffect, useRef, useState } from 'react'
import Sortable from 'sortablejs'

import Tile, { type TileProps } from '../Tile/Tile'
import styles from './_tiles.module.scss'

export type TilesProps = {
  /** Array of Tile components */
  tiles: TileProps[]
  /** Callout made when Tile components are moved */
  onTilesChange: (newTiles: TileProps[]) => void
  /** Optional prop to add a test id to the Tiles for QA testing */
  qaTestId?: string
}

const Tiles = ({
  tiles: initialTiles,
  onTilesChange,
  qaTestId = 'tiles',
}: TilesProps): React.JSX.Element => {
  const [tiles, setTiles] = useState<TileProps[]>(initialTiles)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setTiles(initialTiles)
  }, [initialTiles])

  useEffect(() => {
    if (containerRef.current) {
      const sortable = Sortable.create(containerRef.current, {
        animation: 150,
        onEnd: (evt) => {
          const newTiles = Array.from(tiles)
          const [movedTile] = newTiles.splice(evt.oldIndex ?? 0, 1)
          newTiles.splice(evt.newIndex ?? 0, 0, movedTile)
          setTiles(newTiles)
          onTilesChange(newTiles)
        },
        dragClass: styles.dragging,
      })

      // Cleanup function to destroy Sortable instance on unmount
      return () => {
        sortable.destroy()
      }
    }
  }, [tiles, onTilesChange])

  return (
    <div
      ref={containerRef}
      className={styles.tilesContainer}
      qa-test-id={qaTestId}
    >
      {tiles.map((tile, index) => {
        return (
          <Tile
            key={tile.id}
            {...tile}
            tabIndex={index + 1}
            className={styles.tile}
          />
        )
      })}
    </div>
  )
}

export default Tiles
